/**
 * useGexRay Hook
 *
 * Combines REST API (initial load) + WebSocket (real-time updates) for GEX Ray V3 data.
 * Simpler than useGexSnapshot since the new API returns pre-formatted columnar data.
 *
 * Features:
 * - Initial data load via React Query
 * - Real-time updates via WebSocket subscription (gexray.{mode}.{ticker})
 * - Data stored in refs for efficient updates
 * - Timestamps in milliseconds for direct use with Plotly
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useWebSocket } from '../contexts/WebSocketContext';
import { gexBetaService, subscribeGexRayLive } from '../services/gexBetaService';
import { onQfConnected } from '../services/qfClient';
import type {
  UseGexRayOptions,
  UseGexRayResult,
  GexRayApiResponse,
  GexRayWsUpdate,
} from '../types/gexSnapshot';

/** Full session is ~390 one-minute bars; keep a small cushion, never grow forever. */
export const GEX_RAY_MAX_POINTS = 500;

/** Drop the oldest timeseries columns so live appends cannot grow unbounded. */
export function trimGexRaySeries(data: GexRayApiResponse, max = GEX_RAY_MAX_POINTS): void {
  const excess = data.bucket.length - max;
  if (excess <= 0) return;
  data.bucket.splice(0, excess);
  data.ohlc.open.splice(0, excess);
  data.ohlc.high.splice(0, excess);
  data.ohlc.low.splice(0, excess);
  data.ohlc.close.splice(0, excess);
  data.walls.call_wall_1.splice(0, excess);
  data.walls.call_wall_2.splice(0, excess);
  data.walls.put_wall_1.splice(0, excess);
  data.walls.put_wall_2.splice(0, excess);
  data.net_gex.splice(0, excess);
}

/**
 * Merge a live GEX profile tick into the columnar series.
 *
 * Profile snapshots carry `ohlc: { open/high/low: null, close: spot }`. Writing
 * those onto the series wipes REST `/v1/ohlc` candles; Plotly then drops the
 * bar. Price comes from `/v1/ohlc` + `ohlc:{sym}:1m`, never from profile.
 */
export function applyGexRayWsUpdate(current: GexRayApiResponse, wsData: GexRayWsUpdate): void {
  const lastBucket = current.bucket[current.bucket.length - 1];
  const hasBar =
    wsData.ohlc.open != null || wsData.ohlc.high != null || wsData.ohlc.low != null;

  const writeOhlc = (idx: number): void => {
    if (!hasBar) return;
    current.ohlc.open[idx] = wsData.ohlc.open;
    current.ohlc.high[idx] = wsData.ohlc.high;
    current.ohlc.low[idx] = wsData.ohlc.low;
    current.ohlc.close[idx] = wsData.ohlc.close;
  };

  const writeWalls = (idx: number): void => {
    current.walls.call_wall_1[idx] = wsData.walls.call_wall_1;
    current.walls.call_wall_2[idx] = wsData.walls.call_wall_2;
    current.walls.put_wall_1[idx] = wsData.walls.put_wall_1;
    current.walls.put_wall_2[idx] = wsData.walls.put_wall_2;
    current.net_gex[idx] = wsData.net_gex;
  };

  if (lastBucket !== undefined && wsData.bucket <= lastBucket) {
    const idx = current.bucket.length - 1;
    writeOhlc(idx);
    writeWalls(idx);
  } else {
    current.bucket.push(wsData.bucket);
    current.ohlc.open.push(hasBar ? wsData.ohlc.open : null);
    current.ohlc.high.push(hasBar ? wsData.ohlc.high : null);
    current.ohlc.low.push(hasBar ? wsData.ohlc.low : null);
    current.ohlc.close.push(hasBar ? wsData.ohlc.close : null);
    current.walls.call_wall_1.push(wsData.walls.call_wall_1);
    current.walls.call_wall_2.push(wsData.walls.call_wall_2);
    current.walls.put_wall_1.push(wsData.walls.put_wall_1);
    current.walls.put_wall_2.push(wsData.walls.put_wall_2);
    current.net_gex.push(wsData.net_gex);
    trimGexRaySeries(current);
  }

  current.profile = wsData.profile;
}

/**
 * Get today's date in YYYY-MM-DD format (Eastern Time)
 */
function getTodayET(): string {
  const now = new Date();
  const etString = now.toLocaleString('en-US', { timeZone: 'America/New_York' });
  const et = new Date(etString);
  const year = et.getFullYear();
  const month = String(et.getMonth() + 1).padStart(2, '0');
  const day = String(et.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Hook for GEX Ray V3 data with real-time updates
 */
export function useGexRay(options: UseGexRayOptions): UseGexRayResult {
  const { ticker, mode, date, enabled = true, onUpdate } = options;

  // Determine if we're viewing historical data (not today)
  const isHistorical = date && date !== getTodayET();

  // WebSocket context
  const { isConnected } = useWebSocket();

  // Raw data ref (stores complete API response)
  const dataRef = useRef<GexRayApiResponse | null>(null);

  // Ref to always have latest onUpdate callback (avoids stale closure in WebSocket handler)
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  // Version counter to trigger re-renders
  const [dataVersion, setDataVersion] = useState(0);

  // Last update timestamp
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // Fetch initial data from REST API
  const {
    data: initialData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['gexRayData', ticker, mode, date],
    queryFn: () => gexBetaService.getGexRayData(ticker, mode, date),
    staleTime: isHistorical ? 5 * 60 * 1000 : 60 * 1000, // 5 min for historical, 1 min for live
    // Tab-return / network: REST re-seeds the gap, then live WS owns the tail.
    // Historical dates are a snapshot — don't clobber them on focus/reconnect.
    refetchOnWindowFocus: !isHistorical,
    refetchOnReconnect: !isHistorical,
    enabled: enabled && !!ticker,
  });

  // Update ref when initial data loads
  useEffect(() => {
    if (initialData) {
      dataRef.current = initialData;
      setDataVersion((v) => v + 1);
      if (initialData.bucket.length > 0) {
        setLastUpdate(new Date(initialData.bucket[initialData.bucket.length - 1]));
      }
      // Call onUpdate callback to trigger chart render (bypasses React dependency cycle)
      onUpdateRef.current?.();
    }
  }, [initialData]);

  // Handle WebSocket message - append new data point
  const handleWsMessage = useCallback((msg: { data?: GexRayWsUpdate }) => {
    if (!msg.data || !dataRef.current) return;

    applyGexRayWsUpdate(dataRef.current, msg.data);

    setLastUpdate(new Date(msg.data.bucket));
    setDataVersion((v) => v + 1);

    // Call onUpdate callback to trigger chart render (bypasses React dependency cycle)
    onUpdateRef.current?.();
  }, []);

  // Real-time updates from QuantForge profile:{T} (disabled for historical mode).
  // subscribeGexRayLive emits a flat GexRayWsUpdate; wrap it in the { data } envelope
  // handleWsMessage already consumes. The merge logic below is unchanged — only the
  // source moved off Django-Channels gexray.{mode}.{ticker}.
  useEffect(() => {
    if (!ticker || !enabled || isHistorical) return;
    return subscribeGexRayLive(ticker, mode, (u) => handleWsMessage({ data: u }));
  }, [ticker, mode, enabled, isHistorical, handleWsMessage]);

  // Re-seed REST history whenever QF Centrifugo (re)connects. Live GEX is on
  // QF, not the portal WS — a frozen tab can drop QF while portal stays up,
  // and invalidateQueries on portal reconnect would miss this. Same pattern
  // as Net Sweeper. Skip historical: that query is a dated snapshot.
  useEffect(() => {
    if (!ticker || !enabled || isHistorical) return;
    return onQfConnected(() => {
      void refetch();
    });
  }, [ticker, enabled, isHistorical, refetch]);

  // Get current data (triggers re-render when dataVersion changes)
  const getData = useCallback((): GexRayApiResponse | null => {
    // This line ensures we re-render when dataVersion changes
    void dataVersion;
    return dataRef.current;
  }, [dataVersion]);

  return {
    data: getData(),
    isLoading,
    isInitialLoading: isLoading && !dataRef.current,
    error: error as Error | null,
    isConnected,
    lastUpdate,
    refetch,
  };
}
